"use client"

import { Box, Heading, HStack, Stack, Text, VStack } from "@chakra-ui/react"
import {
  domAnimation,
  LazyMotion,
  m,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion"
import { useEffect, useRef } from "react"
import React from "react"

import BlurText from "@/components/Animations/BlurText"
import ButtonLink from "@/components/ButtonLink/ButtonLink"
import { DocumentTextIcon, NewspaperOutlineIcon } from "@/components/icons"
import AvatarBack from "@/components/Illustrations/Avatar"
import AvatarFront from "@/components/Illustrations/Avatar/AvatarFront"
import Img from "@/components/Img"
import TextHighlighter from "@/components/TextHighlighter"
import { HomeCta } from "@/types/getHome"

export interface LandingHeroContent {
  badgeText: string
  headline: string
  headlineHighlight: string
  intro: string
  primaryCta: HomeCta
  secondaryCta?: HomeCta
}

interface LandingHeroProps {
  data: LandingHeroContent
}

const MotionVStack = m.create(VStack)
const MotionBox = m.create(Box)

type ContainerBounds = {
  left: number
  top: number
  width: number
  height: number
}

const getHighlightedHeadline = (headline: string, highlight: string) => {
  const highlightIndex = headline.toLowerCase().indexOf(highlight.toLowerCase())

  if (highlightIndex === -1) {
    return headline
  }

  return {
    after: headline.slice(highlightIndex + highlight.length),
    before: headline.slice(0, highlightIndex),
    highlight: headline.slice(
      highlightIndex,
      highlightIndex + highlight.length
    ),
  }
}

const STAGGER = 0.07

// ─── Component ───────────────────────────────────────────────────────────────

const HeroV3 = ({ data }: LandingHeroProps) => {
  const headline = getHighlightedHeadline(data.headline, data.headlineHighlight)

  // Number of words before the highlight — used to stagger the highlight delay.
  const beforeWordCount =
    typeof headline !== "string"
      ? headline.before.trim().split(/\s+/).filter(Boolean).length
      : 0

  // Mouse parallax
  const containerRef = useRef<HTMLDivElement>(null)
  const boundsRef = useRef<ContainerBounds | null>(null)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const springConfig = { damping: 25, stiffness: 150 }
  const x = useSpring(mouseX, springConfig)
  const y = useSpring(mouseY, springConfig)
  const xBack = useTransform(x, [-1, 1], [-15, 15])
  const yBack = useTransform(y, [-1, 1], [-15, 15])
  const xImg = useTransform(x, [-1, 1], [-25, 25])
  const yImg = useTransform(y, [-1, 1], [-25, 25])
  const xFront = useTransform(x, [-1, 1], [-35, 35])
  const yFront = useTransform(y, [-1, 1], [-35, 35])

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    let frameId = 0

    const updateBounds = () => {
      const rect = node.getBoundingClientRect()
      boundsRef.current = {
        height: rect.height,
        left: rect.left,
        top: rect.top,
        width: rect.width,
      }
    }

    const scheduleBoundsUpdate = () => {
      cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(updateBounds)
    }

    updateBounds()

    const resizeObserver = new ResizeObserver(scheduleBoundsUpdate)
    resizeObserver.observe(node)
    window.addEventListener("resize", scheduleBoundsUpdate)
    window.addEventListener("scroll", scheduleBoundsUpdate, { passive: true })

    return () => {
      cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
      window.removeEventListener("resize", scheduleBoundsUpdate)
      window.removeEventListener("scroll", scheduleBoundsUpdate)
    }
  }, [])

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const bounds = boundsRef.current
    if (!bounds || bounds.width === 0 || bounds.height === 0) return
    const centerX = bounds.left + bounds.width / 2
    const centerY = bounds.top + bounds.height / 2
    mouseX.set((e.clientX - centerX) / (bounds.width / 2))
    mouseY.set((e.clientY - centerY) / (bounds.height / 2))
  }

  const handleMouseEnter = () => {
    const node = containerRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    boundsRef.current = {
      height: rect.height,
      left: rect.left,
      top: rect.top,
      width: rect.width,
    }
  }

  const handleMouseLeave = () => {
    mouseX.set(0)
    mouseY.set(0)
  }

  return (
    <LazyMotion features={domAnimation}>
      <Stack
        alignItems="center"
        as="section"
        backdropFilter="blur(16px)"
        bgColor={"bg.paper"}
        className="hero-v3-container"
        display="flex"
        flexDirection={{ base: "column-reverse", md: "row" }}
        gap={{ base: 6, md: 6, lg: 8 }}
        justifyContent="center"
        maxW="8xl"
        mx="auto"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        position="relative"
        px={{ base: 8, md: 12, lg: 16 }}
        py={16}
        ref={containerRef}
        rounded="2xl"
        w={{ base: "calc(100% - 1rem)", md: "calc(100% - 3rem)" }}
        zIndex={0}
      >
        {/* Left: Text Content */}
        <VStack
          alignItems="stretch"
          className="hero-v2-content"
          gap={{ base: 2, md: 4 }}
          mx="auto"
          px={{ base: 0 }}
          textAlign="left"
          w={{ base: "100%", md: "55%", lg: "60%" }}
          zIndex={2}
        >
          {/* Heading — word-by-word blur reveal on mount */}
          <Heading
            as="h1"
            color="fg"
            fontWeight="bold"
            size={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl", xl: "6xl" }}
            textAlign="left"
          >
            <span style={{ display: "inline" }}>
              {typeof headline === "string" ? (
                <BlurText stagger={STAGGER} text={headline} />
              ) : (
                <>
                  {headline.before.trim() && (
                    <BlurText stagger={STAGGER} text={headline.before} />
                  )}
                  {headline.before.trim() && " "}
                  {/* Highlight as one block so TextHighlighter spans the full phrase */}
                  <m.span
                    animate={{ filter: "blur(0px)", opacity: 1 }}
                    initial={{ filter: "blur(14px)", opacity: 0 }}
                    style={{ display: "inline-block" }}
                    transition={{
                      delay: beforeWordCount * STAGGER,
                      duration: 0.5,
                      ease: [0.215, 0.61, 0.355, 1],
                    }}
                  >
                    <TextHighlighter
                      action="underline"
                      animationDelay={2000}
                      color="#FF6800"
                    >
                      <Box as="span" color="fg.muted">
                        {headline.highlight}
                      </Box>
                    </TextHighlighter>
                  </m.span>
                  {headline.after.trim() && " "}
                  {headline.after.trim() && (
                    <BlurText
                      delay={(beforeWordCount + 1) * STAGGER}
                      stagger={STAGGER}
                      text={headline.after}
                    />
                  )}
                </>
              )}
            </span>
          </Heading>

          {/* Intro — starts partially visible so content is readable before JS */}
          <m.div
            animate={{ filter: "blur(0px)", opacity: 1 }}
            initial={{ filter: "blur(8px)", opacity: 0.5 }}
            transition={{
              delay: 0.4,
              duration: 0.6,
              ease: [0.215, 0.61, 0.355, 1],
            }}
          >
            <Text
              color="fg.muted"
              fontSize={{ base: "sm", md: "md", lg: "lg" }}
              textAlign="left"
            >
              {data.intro}
            </Text>
          </m.div>

          {/* CTAs — same partial-visibility initial state */}
          <m.div
            animate={{ filter: "blur(0px)", opacity: 1 }}
            initial={{ filter: "blur(8px)", opacity: 0.5 }}
            transition={{
              delay: 0.55,
              duration: 0.6,
              ease: [0.215, 0.61, 0.355, 1],
            }}
          >
            <HStack
              flexGrow={1}
              flexWrap={"wrap"}
              gap={{ base: 2, md: 4 }}
              justifyContent="flex-start"
              mt={8}
            >
              <ButtonLink
                aria-label="Case Studies"
                colorPalette={"primary"}
                data-umami-event={"case-studies"}
                href="/work"
                label={"Case Studies"}
                leadingIcon={<NewspaperOutlineIcon />}
                leadingIconProps={{ size: "md" }}
                mb={{ base: 0 }}
              />
              <ButtonLink
                aria-label="Get my CV"
                colorPalette={"primary"}
                data-umami-event={"get-my-cv"}
                href="/cv"
                label={"Get my CV"}
                leadingIcon={<DocumentTextIcon />}
                leadingIconProps={{ size: "md" }}
                mb={{ base: 0 }}
                variant="subtle"
              />
            </HStack>
          </m.div>
        </VStack>

        {/* Right: Avatar — blur reveal on mount, parallax preserved on inner layers */}
        <MotionBox
          _dark={{
            "--bgCardColor": "colors.gray.800",
            "--bgCardColorDarker": "colors.gray.900",
            "--bgColor": "{colors.gray.900/90}",
            "--shadowColor": "{colors.gray.900/40}",
          }}
          animate={{ filter: "blur(0px)", opacity: 1 }}
          className="lanyard-container"
          css={{
            "--bgCardColor": "colors.white",
            "--bgCardColorDarker": "colors.white",
            "--bgColor": "{colors.gray.400/20}",
            "--shadowColor": "{colors.gray.400/20}",
          }}
          flexShrink={0}
          h={{ base: "210px", md: "320px", lg: "360px" }}
          initial={{ filter: "blur(10px)", opacity: 0 }}
          maxW={{ md: "480px", lg: "600px" }}
          minW={{ md: "320px", lg: "400px" }}
          overflow="visible"
          position="relative"
          px={{ base: 4, md: 0 }}
          transition={{
            delay: 0.1,
            duration: 0.7,
            ease: [0.215, 0.61, 0.355, 1],
          }}
          w={{ base: "100%", md: "45%", lg: "40%" }}
          zIndex={10}
        >
          <MotionVStack
            aria-hidden="true"
            bottom={0}
            className="avatar-back"
            css={{
              "& > svg": {
                filter: "drop-shadow(0 0 0.75rem var(--shadowColor))",
              },
            }}
            justifyContent={"center"}
            left={0}
            position="absolute"
            right={0}
            style={{ x: xBack, y: yBack }}
            top={0}
          >
            <AvatarBack />
          </MotionVStack>
          <MotionVStack
            bottom={0}
            className="avatar-img"
            justifyContent={"center"}
            left={0}
            position="absolute"
            right={0}
            style={{ x: xImg, y: yImg }}
            top={0}
          >
            <Img
              alt="Avatar Illustration"
              breakpointWidths={{
                base: "294",
                sm: "332",
                md: "416",
                lg: "520",
              }}
              fetchPriority="high"
              height={"auto"}
              maxHeight="100%"
              noContainer
              objectFit={"scale-down"}
              originalHeight={750}
              originalWidth={600}
              priority
              sizes="(max-width: 479px) 294px, (max-width: 767px) 332px, (max-width: 1023px) 416px, 520px"
              src={"87b0c048-2cb2-4f86-a900-28a5dcdd715c"}
              transform={{ format: "auto", quality: 82 }}
              width="100%"
            />
          </MotionVStack>
          <MotionVStack
            aria-hidden="true"
            bottom={0}
            className="avatar-front"
            css={{
              "& > svg": {
                filter: "drop-shadow(0 0 0.8rem var(--shadowColor))",
              },
            }}
            justifyContent={"center"}
            left={0}
            position="absolute"
            right={0}
            style={{ x: xFront, y: yFront }}
            top={0}
          >
            <AvatarFront />
          </MotionVStack>
        </MotionBox>
      </Stack>
    </LazyMotion>
  )
}

export default HeroV3
